"use client"

import type { ComponentPropsWithoutRef, ReactNode } from "react"

type Size = "sm" | "md" | "lg"
type State = "default" | "error"

const SIZE_CLS: Record<Size, string> = {
  sm: "px-2.5 py-1.5 text-[13px] rounded-[8px] min-h-[32px]",
  md: "px-3 py-2.5 text-sm rounded-[10px] min-h-[40px]",
  lg: "px-3.5 py-3 text-[15px] rounded-[12px] min-h-[48px]",
}

const STATE_CLS: Record<State, string> = {
  default:
    "border-[var(--redy-border)] focus:border-[var(--redy-charcoal)] focus:[box-shadow:0_0_0_3px_rgba(26,26,26,0.06)]",
  error:
    "border-[var(--redy-error)] focus:border-[var(--redy-error)] focus:[box-shadow:0_0_0_3px_rgba(215,38,61,0.10)]",
}

const BASE =
  "block bg-[var(--redy-surface)] text-[var(--redy-charcoal)] " +
  "placeholder:text-[var(--redy-faint)] border transition-colors " +
  "focus:outline-none disabled:opacity-60 disabled:bg-[var(--redy-warm-white)] disabled:cursor-not-allowed"

// Ancho sin variante: `w-24`, `w-auto`, `w-[110px]`, `!w-24`, `size-8`.
const WIDTH_CLS = /^!?(?:w|size)-/

/** Separa las clases de ancho sin variante del resto: `[ancho, resto]`. */
function splitWidth(className: string): [string[], string[]] {
  const all = className.split(/\s+/).filter(Boolean)
  return [all.filter((c) => WIDTH_CLS.test(c)), all.filter((c) => !WIDTH_CLS.test(c))]
}

export function inputClass(opts?: {
  size?: Size
  state?: State
  className?: string
}) {
  const className = opts?.className ?? ""
  const [width] = splitWidth(className)
  return [
    BASE,
    // `w-full` solo si el caller no trae ancho. Con dos clases de ancho gana la
    // que Tailwind emite última, y dentro de `width` Tailwind 3 las emite en
    // orden alfabético: `w-full` le ganaba a `w-24`, `w-auto`, `w-1/2` y a todo
    // `w-[…]`. Un ancho con variante (`sm:w-auto`) no compite: sale en su media
    // query, después de la base. `min-w-*`/`max-w-*` son otra propiedad.
    width.length ? "" : "w-full",
    SIZE_CLS[opts?.size ?? "md"],
    STATE_CLS[opts?.state ?? "default"],
    className,
  ]
    .filter(Boolean)
    .join(" ")
}

// ── Input ───────────────────────────────────────────────────────────────────

export type InputProps = {
  inputSize?: Size
  state?: State
  leftIcon?: ReactNode
  rightIcon?: ReactNode
} & Omit<ComponentPropsWithoutRef<"input">, "size">

export function Input({
  inputSize = "md",
  state = "default",
  leftIcon,
  rightIcon,
  className = "",
  ...rest
}: InputProps) {
  if (!leftIcon && !rightIcon) {
    return <input className={inputClass({ size: inputSize, state, className })} {...rest} />
  }

  // Con íconos, el ancho del caller va al envoltorio y el <input> lo llena: los
  // íconos se posicionan contra el envoltorio, y con el ancho en el <input> el
  // derecho quedaría fuera del campo, al borde de un div a lo ancho.
  const [width, other] = splitWidth(className)
  const padCls =
    (leftIcon ? " pl-10" : "") + (rightIcon ? " pr-10" : "")
  const cls = inputClass({ size: inputSize, state, className: other.join(" ") + padCls })

  return (
    <div className={["relative", ...width].join(" ")}>
      {leftIcon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--redy-muted)] pointer-events-none flex items-center">
          {leftIcon}
        </span>
      )}
      <input className={cls} {...rest} />
      {rightIcon && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--redy-muted)] flex items-center">
          {rightIcon}
        </span>
      )}
    </div>
  )
}

// ── Select ──────────────────────────────────────────────────────────────────

export type SelectProps = {
  selectSize?: Size
  state?: State
} & Omit<ComponentPropsWithoutRef<"select">, "size">

export function Select({
  selectSize = "md",
  state = "default",
  className = "",
  children,
  ...rest
}: SelectProps) {
  return (
    <select
      className={inputClass({
        size: selectSize,
        state,
        className: className + " appearance-none bg-[length:14px] bg-[right_12px_center] bg-no-repeat pr-9",
      })}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239B9690' stroke-width='2'><path stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/></svg>\")",
      }}
      {...rest}
    >
      {children}
    </select>
  )
}

// ── Textarea ────────────────────────────────────────────────────────────────

export type TextareaProps = {
  textareaSize?: Size
  state?: State
} & ComponentPropsWithoutRef<"textarea">

export function Textarea({
  textareaSize = "md",
  state = "default",
  className = "",
  ...rest
}: TextareaProps) {
  return (
    <textarea
      className={inputClass({
        size: textareaSize,
        state,
        className: className + " min-h-[96px] resize-y leading-relaxed",
      })}
      {...rest}
    />
  )
}

// ── FormField ───────────────────────────────────────────────────────────────

export type FormFieldProps = {
  label?: string
  helper?: string
  error?: string
  required?: boolean
  htmlFor?: string
  children: ReactNode
  className?: string
}

export function FormField({
  label,
  helper,
  error,
  required,
  htmlFor,
  children,
  className = "",
}: FormFieldProps) {
  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--redy-charcoal)] mb-1.5 block"
        >
          {label}
          {required && (
            <span className="text-[var(--redy-error)] ml-0.5" aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <p
          className="text-[11px] text-[var(--redy-error)] mt-1"
          role="alert"
        >
          {error}
        </p>
      ) : helper ? (
        <p className="text-[11px] text-[var(--redy-muted)] mt-1">{helper}</p>
      ) : null}
    </div>
  )
}
